<script lang="ts">
import { defineComponent } from 'vue';
import { DomainConfig } from '@/util/env';
import { EAV, SearchRepo } from '@fibl/zdb-common';
import { Menu, Constants } from '@fibl/oxs-common';
import autstaticSvg from '@/assets/svg/AutStatics.svg';
import laenderauswahlSvg from '@/assets/svg/LinkOXS.svg';
import nationaDatabaseSvg from '@/assets/svg/NationalDatabase.svg';
import offerlistSvg from '@/assets/svg/Antragsstellung.svg';
import loginSvg from '@/assets/svg/Login.svg';

export default defineComponent({
	name: 'ImportantRDBLinks',

	data: () => ({
		menuItems: [] as EAV.Entity<Menu.Menu>[],
		autstaticSvg,
		offerlistSvg,
		laenderauswahlSvg,
		nationaDatabaseSvg,
		loginSvg,
	}),
	created() {
		this.init();
	},
	computed: {
		website() {
			return this.$globalStore.websiteStore.websiteCode;
		},
		countryCode() {
			return this.$globalStore.websiteStore.countryCode;
		},
		isGuest() {
			return this.$globalStore.tokenStore.isGuest;
		},
		isSup() {
			return this.$globalStore.tokenStore.role === 'sup';
		},
		isAut() {
			return this.$globalStore.tokenStore.role === 'aut';
		},
		loggedIn() {
			return (
				this.$globalStore.tokenStore.ready && !!this.$globalStore.tokenStore.main.account
			);
		},
		oxsUrl() {
			const oxsRoute = Object.entries(DomainConfig).find(([_, code]) => code === 'OXSSELECT');
			if (oxsRoute) {
				const l = document.location;
				let url = new URL(`${l.protocol}//${oxsRoute[0]}`);
				if (url.hostname === l.hostname) {
					url.port = l.port;
				}
				return url.href;
			}
			return null;
		},
		rootItems() {
			return this.menuItems
				.filter((e) => !e.data.parent)
				.sort((a, b) => a.data.sorting - b.data.sorting);
		},
		childrenItems() {
			for (let entry of this.rootItems) {
				return this.menuItems
					.filter((e) => e.data.parent === entry.id)
					.sort((a, b) => a.data.sorting - b.data.sorting);
			}
			return [];
		},
	},

	methods: {
		async init() {
			console.log('menu init', this.website);
			if (![Constants.WEB_OXS].includes(this.website)) return;
			let filter: SearchRepo.SearchFilter = { $and: [] };
			filter.$and.push({
				field: '/data/website',
				comparison: 'eq',
				value: this.website,
			});
			if (this.website === Constants.WEB_OXS) {
				filter.$and.push({
					field: '/data/country',
					comparison: 'eq',
					value: this.countryCode,
				});
			}
			this.menuItems = await SearchRepo.findAll<Menu.Menu>('menu', filter);
		},
		routeToLanguageSelect() {
			if (this.oxsUrl) window.location.href = this.oxsUrl;
		},
		ssoLogin() {
			this.$globalStore.tokenStore.authenticate();
		},
		routeToAutOffer() {
			this.$router.push({ name: 'OfferApproval' });
		},
		routeToSupOffer() {
			this.$router.push({ name: 'OfferList' });
		},
		routeToNationalDataBase() {
			window.open(
				'https://agriculture.ec.europa.eu/farming/organic-farming/organic-production-and-products/organics-references_en',
				'_blank',
			);
		},
		openSelfRegisterSup() {
			this.$router.push({ name: 'RegisterSup' });
		},
	},
});
</script>

<template>
	<div class="rounded-md shadow-md overflow-hidden mb-4">
		<el-carousel
			height="500px"
			trigger="click"
		>
			<el-carousel-item>
				<img src="@/assets/images/rdb1.jpg" />
			</el-carousel-item>
			<el-carousel-item>
				<img src="@/assets/images/rdb2.jpg" />
			</el-carousel-item>
			<el-carousel-item>
				<img src="@/assets/images/rdb3.jpg" />
			</el-carousel-item>
		</el-carousel>
	</div>
	<div class="grid grid-flow-col auto-cols-fr gap-4 items-baseline">
		<el-link
			v-if="!loggedIn && !isGuest"
			@click="ssoLogin">
			<div class="flex flex-col items-center p-3 mb-3">
				<div class="pictogram mb-2">
					<el-image :src="loginSvg" style="width: 100px; height: 100px;"/>
				</div>
				<span class="text-center">{{ $t('components.menu.login.login') }}</span>
			</div>
		</el-link>
		<el-link
			v-if="isGuest && !loggedIn"
			@click="openSelfRegisterSup">
			<div class="flex flex-col items-center p-3 mb-3">
				<div class="pictogram mb-2">
					<el-image :src="loginSvg" style="width: 100px; height: 100px;"/>
				</div>
				<span class="text-center">{{ $t('components.menu.login.selfSup') }}</span>
			</div>
		</el-link>
		<template v-else-if="!isGuest && loggedIn">
			<el-link
				v-if="isAut"
				@click="routeToAutOffer"
			>
				<div class="flex flex-col items-center p-3 mb-3">
					<div class="pictogram mb-2">
						<el-image :src="offerlistSvg" style="width: 100px; height: 100px;"/>
					</div>
					<span class="text-center">{{ $t('components.menu.offerCheck') }}</span>
				</div>
			</el-link>
			<el-link
				v-if="isSup"
				@click="routeToSupOffer"
			>
				<div class="flex flex-col items-center p-3 mb-3">
					<div class="pictogram mb-2">
						<el-image :src="offerlistSvg" style="width: 100px; height: 100px;"/>
					</div>
					<span class="text-center">{{ $t('components.menu.offerCreate') }}</span>
				</div>
			</el-link>
		</template>
		<el-link @click="routeToLanguageSelect">
			<div class="flex flex-col items-center p-3 mb-3">
				<div class="pictogram mb-2">
					<el-image :src="laenderauswahlSvg" style="width: 100px; height: 100px;"/>
				</div>
				<span class="text-center">{{ $t('components.menu.languageSelect') }}</span>
			</div>
		</el-link>
		<el-link @click="routeToNationalDataBase">
			<div class="flex flex-col items-center p-3 mb-3">
				<div class="pictogram mb-2">
					<el-image :src="nationaDatabaseSvg" style="width: 100px; height: 100px;"/>
				</div>
				<span class="text-center">{{ $t('components.menu.nationalDatabase') }}</span>
			</div>
		</el-link>
	</div>
	<el-divider />
</template>
